import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";

/**
 * Props:
 * - open: boolean
 * - slotId: string | number
 * - slotLabel: string
 * - initialValues?: { nombre?: string, documento?: string }  // para ocupar o para liberar (puede venir prellenado)
 * - onCancel: () => void
 * - onConfirm: (payload: { slotId, slotLabel, nombre, documento }) => void
 */
export default function ReservaUbicacionDialog({
  open,
  title,
  slotId,
  slotLabel,
  visitante,
  editable,
  devolucion,
  onCancel,
  onConfirm,
  onScanDNI,
}) {
  // const inputRef = React.useRef(null);
  const [nombre, setNombre] = React.useState(visitante?.nombre || "");
  const [documento, setDocumento] = React.useState(visitante?.documento || "");
  const [touched, setTouched] = React.useState({ nombre: false, documento: false });

  React.useEffect(() => {
    if (!open) return;
    setNombre(visitante?.nombre || "");
    setDocumento(visitante?.documento || "");
    setTouched({ nombre: false, documento: false });
  }, [open, visitante]);

  // const normalizarDocumento = (v) => v.replace(/[^\d]/g, "");

  const adpotarDatosDelDNI = (data) => {
    // @30926777    @A@1@TAGLIAZUCCHI@MATIAS EDUARDO@ARGENTINA@06-05-1984@M@14-04-2011@00045699471@8028 @14-04-2026@697@0@ILRÑ2.01 CÑ110223.01 )No Cap.=@UNIDAD #16 ]] S-NÑ 0040:2008::0021
    // 00319299845@BORELLI@JAVIER ALEJANDRO@M@25380486@A@26-05-1976@06-12-2014@207
    const DNI = data.split('@');
    let nombreStr = '';
    let documentoStr = '';
    if (DNI[0] == '') {
      nombreStr = (DNI[5] ?? '') + ' ' + (DNI[4] ?? '');
      documentoStr = DNI[1]?.trim() ?? '';
    } else {
      nombreStr = (DNI[2] ?? '') + ' ' + (DNI[1] ?? '');
      documentoStr = DNI[4] ?? '';
    }
    setNombre(nombreStr ?? '');
    setDocumento(documentoStr ?? '');
  }

  const normalizarDocumento = (v) => {
    if (v.includes('@')) {
      // Es un DNI escaneado
    } else return v.replace(/[^\w]/g, "")
  };

  const errores = React.useMemo(() => {
    const n = nombre.trim();
    const d = documento.trim();

    return {
      nombre: n.length === 0 ? "Ingresá el nombre" : "",
      documento:
        d.length === 0
          ? "Ingrese el documento"
          // : !/^\d{6,12}$/.test(d)
          : !/^\w{6,12}$/.test(d)
          ? "Documento inválido (6 a 12 caracteres)"
          : "",
    };
  }, [nombre, documento]);

  const puedeConfirmar = Boolean(slotId) && !errores.nombre && !errores.documento;

  const handleEscanearDNI = () => {
    onScanDNI?.();
  }

  const handleConfirmar = () => {
    setTouched({ nombre: true, documento: true });
    if (!puedeConfirmar) return;

    onConfirm?.({
      slotId,
      slotLabel: slotLabel || String(slotId),
      nombre: nombre.trim(),
      documento: documento.trim(),
    });
  };

  const handleKeyUp = (event) => {
    if (event.key == 'Enter') {
      if (event.target.value.includes('@')) {
        adpotarDatosDelDNI(event.target.value);
      }
    }
  }

  // React.useEffect(() => {
  //   inputRef.current?.focus();
  // }, []);

  return (
    <Dialog
      disableRestoreFocus
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="sm"
      sx={{zIndex: 10}}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        {title}
        <IconButton
          onClick={onCancel}
          aria-label="Cerrar"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Complete los datos del visitante.
          </Typography>

          <TextField
            label="Ubicación"
            value={slotLabel || (slotId != null ? String(slotId) : "")}
            slotProps={{
              input: {
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <PlaceOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}}
          />

          <TextField
            label="Documento"
            value={documento}
            // ref={inputRef}
            autoFocus
            onChange={(e) => setDocumento(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, documento: true }))}
            onKeyUp={handleKeyUp}
            error={touched.documento && Boolean(errores.documento)}
            helperText={touched.documento ? errores.documento : " "}
            // inputMode="numeric"
            slotProps={{
              input: {
                readOnly: !!visitante,
                inputMode: 'numeric',
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }
            }}
          />

          <TextField
            label="Nombre y apellido"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, nombre: true }))}
            onKeyUp={handleKeyUp}
            error={touched.nombre && Boolean(errores.nombre)}
            helperText={touched.nombre ? errores.nombre : " "}
            slotProps={{
              input: {
                readOnly: !!visitante,
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon fontSize="small" />
                  </InputAdornment>
                ),
            }}}
          />

        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {/* {!visitante && <Button onClick={handleEscanearDNI} variant="outlined">
          Escanear DNI
        </Button>} */}
        <Button onClick={onCancel} variant="outlined">
          Cancelar
        </Button>
        {!!devolucion
          ? <Button onClick={handleConfirmar} disabled={!puedeConfirmar} variant="contained" color={'error'}>
            Devolver
          </Button>
          : <Button onClick={handleConfirmar} disabled={!puedeConfirmar} variant="contained" >
            Confirmar
          </Button>
        }
      </DialogActions>
    </Dialog>
  );
}
